/**
 * @file RepeatingBillModal.js
 * @version 1.1.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-03-27
 * @modified 2026-09-16
 * @description View-only modal for repeating bills synced from Xero
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './BillModal.css';
import LoadingSpinner from './LoadingSpinner';
import { formatCurrency, formatPrice } from '../utils/currencyFormatter';
import { formatQuantity } from '../utils/numberFormatters';
import { formatDateShort } from '../utils/dateFormatters';
import { getTaxPercentDisplay } from '../utils/taxTypes';
import { useSlideOutButton } from './SlideOutButton';

function RepeatingBillModal({ billId, onClose }) {
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [showLines, setShowLines] = useState(false);

  useSlideOutButton([bill?.lines?.length]);

  useEffect(() => {
    if (billId) {
      fetchBillDetails();
      document.body.classList.add('modal-open');
      const cw = document.querySelector('.content-wrapper');
      if (cw) cw.classList.add('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
      const cw = document.querySelector('.content-wrapper');
      if (cw) cw.classList.remove('modal-open');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billId]);

  const fetchBillDetails = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/repeating-bills/${billId}`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) setBill(data.repeatingBill);
    } catch (error) {
      console.error('Failed to fetch repeating bill:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      document.body.classList.remove('modal-open');
      const cw = document.querySelector('.content-wrapper');
      if (cw) cw.classList.remove('modal-open');
      onClose();
    }, 300);
  };

  const getStatusBadgeClass = (status) => {
    const map = { DRAFT: 'draft', AUTHORISED: 'authorised', DELETED: 'deleted' };
    return `status-badge ${map[(status || '').toUpperCase()] || 'default'}`;
  };

  const formatSchedule = () => {
    if (!bill?.schedule_period || !bill?.schedule_unit) return '-';
    const unit = (bill.schedule_unit || '').toLowerCase();
    const period = bill.schedule_period;
    const unitLabel = unit === 'monthly' ? 'month' : unit === 'weekly' ? 'week' : unit;
    return `Every ${period} ${unitLabel}${period > 1 ? 's' : ''}`;
  };

  const formatDueDate = () => {
    if (!bill?.due_date_type) return '-';
    const day = bill.due_date_day || 0;
    const typeMap = {
      'DAYSAFTERBILLDATE': `${day} day${day !== 1 ? 's' : ''} after bill date`,
      'DAYSAFTERBILLMONTH': `${day} day${day !== 1 ? 's' : ''} after bill month`,
      'OFCURRENTMONTH': `Day ${day} of current month`,
      'OFFOLLOWINGMONTH': `Day ${day} of following month`
    };
    return typeMap[bill.due_date_type] || bill.due_date_type;
  };

  if (!billId) return null;

  return createPortal(
    <>
      <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
        <div
          className={`modal-content bill-modal ${closing ? 'closing' : ''} ${showLines ? 'expanded' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >

          {/* ── LEFT PANEL ── */}
          <div className="bill-panel-left">

            {/* Header */}
            <div className="panel-header">
              <div className="panel-header-top">
                <div className="panel-header-title">
                  <h2>{loading ? 'Loading...' : (bill?.supplier_name || 'Repeating Bill')}</h2>
                  {!loading && bill && (
                    <div className="panel-header-badges">
                      <span className={getStatusBadgeClass(bill.status)}>{bill.status}</span>
                    </div>
                  )}
                </div>
                <div className="panel-close-buttons">
                  <button className="btn-icon close-btn" onClick={handleClose}>
                    <span className="material-symbols-rounded">close</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="panel-body">
              {loading ? (
                <LoadingSpinner />
              ) : bill ? (
                <>
                  {/* Schedule */}
                  <div className="detail-section">
                    <h3>Schedule</h3>
                    <div className="detail-grid">
                      <div className="detail-row">
                        <span className="label">Frequency</span>
                        <span className="value">{formatSchedule()}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Due Date</span>
                        <span className="value">{formatDueDate()}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Start Date</span>
                        <span className="value">{formatDateShort(bill.start_date)}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Next Bill Date</span>
                        <span className="value">{formatDateShort(bill.next_scheduled_date)}</span>
                      </div>
                      {bill.end_date && (
                        <div className="detail-row">
                          <span className="label">End Date</span>
                          <span className="value">{formatDateShort(bill.end_date)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Supplier */}
                  <div className="detail-section">
                    <h3>Supplier</h3>
                    <div className="detail-grid">
                      <div className="detail-row">
                        <span className="label">Name</span>
                        <span className="value">{bill.account_company_name || bill.supplier_name || '-'}</span>
                      </div>
                      {bill.account_no && (
                        <div className="detail-row">
                          <span className="label">Account No</span>
                          <span className="value">{bill.account_no}</span>
                        </div>
                      )}
                      {bill.reference && (
                        <div className="detail-row">
                          <span className="label">Reference</span>
                          <span className="value">{bill.reference}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="bill-totals">
                    <div className="totals-row">
                      <span>Subtotal</span>
                      <span>{formatCurrency(bill.sub_total)}</span>
                    </div>
                    <div className="totals-row">
                      <span>VAT</span>
                      <span>{formatCurrency(bill.total_tax)}</span>
                    </div>
                    <div className="totals-row totals-row-total">
                      <span>Total</span>
                      <span>{formatCurrency(bill.total)}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="no-data">Repeating bill not found</div>
              )}
            </div>

            {/* Footer */}
            {!loading && bill && (
            <div className="panel-nav-buttons">
              <div style={{ display: 'flex', flexDirection: 'row', gap: 'var(--spacing-md)', justifyContent: 'flex-start' }}>
                {bill?.lines?.length > 0 && (
                  <div>
                    <button className="btn-slideout" onClick={() => setShowLines(!showLines)}>
                      <div className="icon-wrapper">
                        <span className="material-symbols-rounded">receipt_long</span>
                      </div>
                      <span className="btn-label">Line Items</span>
                      <span className="btn-badge">{bill.lines.length}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>

          {/* ── RIGHT PANEL (Line Items) ── */}
          {showLines && bill?.lines?.length > 0 && (
            <div className="bill-panel-right" style={{ width: showLines ? 'calc(90vw - 420px - 1px)' : 0 }}>
              <div className="right-panel-header">
                <h3>Line Items</h3>
              </div>
              <div className="panel-content-section" style={{ overflow: 'auto', padding: 'var(--spacing-lg)' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Item Code</th>
                      <th className="text-right">Qty</th>
                      <th className="text-right">Unit Amount</th>
                      <th className="text-right">Line Amount</th>
                      <th className="text-right">Tax %</th>
                      <th>Account</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bill.lines.map((line, idx) => (
                      <tr key={idx}>
                        <td>{line.description || '-'}</td>
                        <td>{line.item_code || '-'}</td>
                        <td className="text-right">{formatQuantity(line.quantity) || '-'}</td>
                        <td className="text-right">{formatPrice(line.unit_amount)}</td>
                        <td className="text-right">{formatCurrency(line.line_amount)}</td>
                        <td className="text-right">{getTaxPercentDisplay(line.tax_type)}</td>
                        <td>{line.account_code || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}

export default RepeatingBillModal;
